<template>
  <div
    :class="inline ? 'timeslot-inline-root' : 'timeslot-overlay'"
    @click.self="!inline && closePopup()"
  >
    <div
      class="timeslot-popup"
      :class="{
        'timeslot-popup--phone-step': step === 1,
        'timeslot-popup--inline': inline
      }"
      :style="panelStyle"
    >
      <div class="popup-header" v-if="step === 0">
        <div class="header-spacer"></div>
        <h2 :style="{ color: headingColor }">
          {{ $t('Kiosk.selectTimeSlot') }}
        </h2>
      </div>
      
      <!-- Step 0: departments (horizontal) + calendar + time slots -->
      <div v-if="step === 0" class="step1-flow">
        <!-- Show departments horizontally at top if multiple -->
        <div v-if="departments && departments.length > 1" class="departments-horizontal" :class="{ 'is-loading': slotsLoading }">
          <button
            v-for="dept in departments"
            :key="dept.id"
            type="button"
            class="department-item-horizontal"
            :disabled="slotsLoading"
            @click="selectDepartment(dept)"
            :style="getDepartmentStyleHorizontal(dept)"
          >
            <span class="dept-label">{{ getDepartmentLabel(dept) }}</span>
          </button>
        </div>
        
        <div class="week-strip" :class="{ 'is-loading': slotsLoading }">
          <div class="week-strip-header">
            <button type="button" class="week-nav-btn" :disabled="slotsLoading" @click="goPrevWeek" :aria-label="$t('Kiosk.backButton')">
              <svg class="week-nav-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/></svg>
            </button>
            <div class="week-month-label" :style="{ color: headingColor }">{{ monthYearLabel }}</div>
            <button type="button" class="week-nav-btn" :disabled="slotsLoading" @click="goNextWeek" :aria-label="$t('Kiosk.nextButton')">
              <svg class="week-nav-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg>
            </button>
          </div>
          <div class="week-days-row">
            <button
              v-for="(d, idx) in weekDays"
              :key="idx"
              type="button"
              class="day-cell"
              :class="{
                'day-cell--selected': isSameDay(d, selectedDate),
                'day-cell--past': isPastDay(d)
              }"
              :disabled="slotsLoading || isPastDay(d)"
              @click="selectDay(d)"
            >
              <span class="day-cell-dow" :style="dayDowStyle(d)">{{ shortWeekday(d) }}</span>
              <span
                class="day-cell-dom"
                :style="dayDomStyle(d)"
              >{{ d.getDate() }}</span>
            </button>
          </div>
        </div>
        
        <!-- Show time slots only after department is selected or if only one department -->
        <div v-if="selectedDepartment" ref="timeslotsArea" class="timeslots-area">
          <div v-if="slotsFetchError" class="availability-error-message">
            <p :style="{ color: '#c0392b' }">{{ slotsFetchError }}</p>
          </div>
          <div
            v-else-if="slotsLoading"
            class="timeslots-loading"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div class="timeslot-spinner" :style="{ borderTopColor: secondaryColor, borderRightColor: secondaryColor }" />
            <p class="timeslots-loading__label" :style="{ color: headingColor }">
              {{ $t('Kiosk.loadingAvailability') }}
            </p>
            <span class="visually-hidden">{{ $t('Kiosk.loadingAvailability') }}</span>
          </div>
          <div
            v-else
            class="timeslots-container"
            :class="{ 'timeslots-container--single': isSingleSlotLayout }"
            :style="slotGridStyle"
          >
            <button
              v-for="slot in timeSlots"
              :key="slot.value"
              type="button"
              class="timeslot-item"
              :disabled="slot.disabled"
              @click="selectSlot(slot)"
              :style="[getSlotStyle(slot), slotItemFontStyle, slotItemLayoutStyle]"
            >
              <span class="slot-time">{{ slot.label }}</span>
            </button>
          </div>
        </div>
        
        <!-- Show message when no department selected -->
        <div v-else-if="departments && departments.length > 1" class="no-department-message">
          <p :style="{ color: headingColor }">{{ $t('Kiosk.selectDepartment')}}</p>
        </div>
      </div>

      <!-- Step 1: phone input -->
      <div v-if="step === 1" class="phone-input-container">
        <div class="phone-step-layout">
          <div
            class="phone-info-panel"
            :style="{ borderRight: `1px solid ${effectiveTextColor}` }"
          >
            <div class="phone-info-content">
              <h3 class="phone-step-title" :style="{ color: effectiveTextColor }">
                {{ $t('Kiosk.inputNumberTitleBooking') }}
              </h3>
              <p class="phone-hint agreement-text" :style="{ color: effectiveTextColor }">
                {{ $t('Kiosk.acceptTermsFirstPart') }}
                <span :style="{ color: termsAccentColor }">"{{ $t('Kiosk.sendButton') }}"</span>
                {{ $t('Kiosk.acceptTermsSecondPart') }}
              </p>

              <div class="appointment-recap">
                <div class="appointment-recap__accent" :style="{ background: secondaryColor }"></div>
                <div class="appointment-recap__content">
                  <span class="appointment-recap__label" :style="{ color: secondaryColor }">
                    {{ $t('Kiosk.yourAppointment') }}
                  </span>
                  <p
                    v-if="selectedServiceLabel"
                    class="appointment-recap__service"
                    :style="{ color: effectiveTextColor }"
                  >
                    {{ selectedServiceLabel }}
                  </p>
                  <p class="appointment-recap__date" :style="{ color: effectiveTextColor }">
                    {{ formattedSelectionDate }}
                  </p>
                  <div class="appointment-recap__time-block">
                    <span
                      class="appointment-recap__time time-text"
                      :style="{ color: secondaryColor }"
                    >
                      {{ selectedSlot }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="phone-info-footer">
              <button
                type="button"
                class="btn-back-phone"
                @click="handleBack"
                :style="{
                  color: effectiveTextColor,
                  border: `1px solid ${effectiveTextColor}`
                }"
              >
                {{ $t('Kiosk.backButton') }}
              </button>
            </div>
          </div>

          <div class="phone-keypad-panel" @click="$emit('playSound')">
            <lbrx-phone-number-input
              ref="phoneInput"
              :theme-template="phoneThemeTemplate"
              :model-value="false"
              :no-anonym="false"
              @send-ticket="onPhonePadSend"
              @play-sound="$emit('playSound')"
            />
          </div>
        </div>
      </div>

      <div v-if="step === 0" class="popup-actions">
        <button 
          class="btn-cancel" 
          @click="handleBack"
          :style="backButtonStyle"
        >
          {{ selectedDepartment && departments && departments.length > 1 ? $t('Kiosk.backButton') : $t('Kiosk.backButton') }}
        </button>
        <button 
          class="btn-confirm" 
          @click="handleNext"
          :disabled="!selectedSlot"
          :style="nextButtonStyle"
        >
          {{ $t('Kiosk.nextButton') || 'Next' }}
        </button>
      </div>
    </div>
    
    <!-- Success Popup -->
    <lbrx-booking-success-popup
      v-model="showSuccessPopup"
      :auto-close-delay="5000"
      @close="handleSuccessClose"
    />
  </div>
  
  <!-- Loading Popup using LbrxKioskPopup -->
  <lbrx-kiosk-popup 
    v-if="isCreatingBooking" 
    v-model="isCreatingBooking"
    :closable="false"
    :box-height="'40%'"
    :content="loadingMessage"
    :icon="'loader'"
    :text-color="primaryColor"
    :box-background="'#FFFFFF'"
    :icon-color="primaryColor"
  />
  
  <!-- Error Popup -->
  <lbrx-kiosk-popup 
    v-if="showErrorPopup" 
    v-model="showErrorPopup"
    @close="closeErrorPopup"
    :closable="true"
    :box-height="'40%'"
    :content="errorMessage"
    :icon="'error'"
    :text-color="'#EB5757'"
    :box-background="'#FFFFFF'"
    :icon-color="'#EB5757'"
  />
</template>

<script>
import moment from 'moment';
import LbrxPhoneNumberInput from '@/components/LbrxPhoneNumberInput.vue';
import LbrxBookingSuccessPopup from '@/components/LbrxBookingSuccessPopup.vue';
import LbrxKioskPopup from '@/components/LbrxKioskPopup.vue';
import { kioskService } from '@/_services';

export default {
  name: 'LbrxTimeSlotPopup',
  components: { 
    LbrxPhoneNumberInput, 
    LbrxBookingSuccessPopup,
    LbrxKioskPopup
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    primaryColor: {
      type: String,
      default: '#007bff'
    },
    secondaryColor: {
      type: String,
      default: '#6c757d'
    },
    textColor: {
      type: String,
      default: '#ffffff'
    },
    boxBackground: {
      type: String,
      default: '#ffffff'
    },
    startHour: {
      type: Number,
      default: 8
    },
    endHour: {
      type: Number,
      default: 17
    },
    interval: {
      type: Number,
      default: 30
    },
    disabledSlots: {
      type: Array,
      default: () => []
    },
    themeTemplate: {
      type: Object,
      default: null
    },
    slotBorderAccent: {
      type: String,
      default: '#c9a062'
    },
    hidePastSlots: {
      type: Boolean,
      default: true
    },
    departments: {
      type: Array,
      default: () => []
    },
    selectedService: {
      type: Object,
      default: null
    },
    inline: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'close', 'confirm', 'playSound'],
  watch: {
    // With v-if on the parent, this component mounts with modelValue already true.
    // A non-immediate watcher never runs on that first open, so availability is never fetched.
    modelValue: {
      immediate: true,
      handler(val) {
        if (!val) return;
        const today = new Date();
        this.weekStart = this.startOfWeekMonday(today);
        this.selectedDate = new Date(today);
        this.selectedSlot = null;
        this.slotsFetchError = null;

        if (this.departments && this.departments.length > 0) {
          this.selectedDepartment = this.departments[0];
          this.fetchAvailableSlots();
        } else {
          this.selectedDepartment = null;
        }

        this.step = 0;
        this.scheduleSlotGridLayout();
      }
    },
    timeSlots() {
      this.scheduleSlotGridLayout();
    },
    slotsLoading(val) {
      if (!val) {
        this.scheduleSlotGridLayout();
      }
    },
    step(val) {
      if (val === 0) {
        this.scheduleSlotGridLayout();
      }
    }
  },
  data() {
    return {
      selectedSlot: null,
      timeSlots: [],
      slotsFetchError: null,
      step: 0,
      weekStart: null,
      selectedDate: null,
      selectedDepartment: null,
      slotsLoading: false,
      isCreatingBooking: false,
      showSuccessPopup: false,
      bookingData: null,
      loadingMessage: '',
      slotGridCols: 6,
      slotGridRows: 2,
      slotFontSize: '28px',
      isSingleSlotLayout: false,
      singleSlotWidth: 'auto',
      singleSlotHeight: 'auto',
      showErrorPopup: false,
      errorMessage: ''
    };
  },
  computed: {
    slotGridStyle() {
      if (this.isSingleSlotLayout) {
        return {};
      }
      return {
        gridTemplateColumns: `repeat(${this.slotGridCols}, 1fr)`,
        gridTemplateRows: `repeat(${this.slotGridRows}, auto)`
      };
    },
    slotItemFontStyle() {
      return {
        fontSize: this.slotFontSize
      };
    },
    slotItemLayoutStyle() {
      if (!this.isSingleSlotLayout) {
        return {};
      }
      return {
        width: this.singleSlotWidth,
        height: this.singleSlotHeight,
        flexShrink: 0
      };
    },
    weekDays() {
      if (!this.weekStart) return [];
      const days = [];
      for (let i = 0; i < 7; i++) {
        days.push(this.addDays(this.weekStart, i));
      }
      return days;
    },
    monthYearLabel() {
      if (!this.weekDays.length) return '';
      const a = this.weekDays[0];
      const b = this.weekDays[6];
      let loc = this.$i18n.locale;
      if (loc === 'ar') loc += '-tn';
      const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
      if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
        return cap(moment(a).locale(loc).format('MMM')) + ', ' + a.getFullYear();
      }
      return `${cap(moment(a).locale(loc).format('D MMM'))} – ${cap(moment(b).locale(loc).format('D MMM YYYY'))}`;
    },
    formattedSelectionDate() {
      if (!this.selectedDate) return '';
      let loc = this.$i18n.locale;
      if (loc === 'ar') loc += '-tn';
      return moment(this.selectedDate).locale(loc).format('dddd D MMMM YYYY');
    },
    termsAccentColor() {
      if (
        this.themeTemplate &&
        this.themeTemplate.printTicket &&
        this.themeTemplate.printTicket.secondaryTextColor
      ) {
        return this.themeTemplate.printTicket.secondaryTextColor;
      }
      return this.secondaryColor;
    },
    selectedServiceLabel() {
      return this.getServiceLabel(this.selectedService);
    },
    printTicketBackground() {
      const printTicket = this.themeTemplate && this.themeTemplate.printTicket;
      if (!printTicket) {
        return this.boxBackground;
      }
      if (printTicket.backgroundImage) {
        return `url(${printTicket.backgroundImage})`;
      }
      return printTicket.background || this.boxBackground;
    },
    printTicketTextColor() {
      const printTicket = this.themeTemplate && this.themeTemplate.printTicket;
      if (!printTicket) {
        return this.textColor;
      }
      if (printTicket.backgroundImage && this.themeTemplate.textColor) {
        return this.themeTemplate.textColor;
      }
      return printTicket.textColor || this.textColor;
    },
    effectiveTextColor() {
      return this.inline ? this.printTicketTextColor : this.textColor;
    },
    headingColor() {
      return this.inline ? this.printTicketTextColor : this.primaryColor;
    },
    panelStyle() {
      if (this.inline) {
        return {
          background: this.printTicketBackground,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat'
        };
      }
      return {
        background: this.step === 1 ? this.primaryColor : this.boxBackground
      };
    },
    backButtonStyle() {
      if (this.inline) {
        return {
          color: this.effectiveTextColor,
          border: `1px solid ${this.effectiveTextColor}`
        };
      }
      return {
        color: this.primaryColor,
        border: `2px solid ${this.primaryColor}`
      };
    },
    servicesTheme() {
      return (this.themeTemplate && this.themeTemplate.services) || {};
    },
    slotBackground() {
      const services = this.servicesTheme;
      const serviceBg = services.serviceBackground;
      const pageBg = services.background;

      if (
        pageBg &&
        String(pageBg).includes('base64') &&
        serviceBg &&
        !String(serviceBg).includes('base64')
      ) {
        return this.resolveThemeBackground(pageBg, '#ffffff');
      }
      return this.resolveThemeBackground(serviceBg, '#ffffff');
    },
    slotTextColor() {
      const services = this.servicesTheme;
      const serviceBg = services.serviceBackground;
      const pageBg = services.background;

      if (
        (serviceBg && String(serviceBg).includes('base64')) ||
        (pageBg && String(pageBg).includes('base64'))
      ) {
        return (this.themeTemplate && this.themeTemplate.textColor) || this.textColor;
      }
      return services.serviceTextColor || this.primaryColor;
    },
    selectedSlotBackground() {
      return this.resolveThemeBackground(
        this.servicesTheme.selectedServiceBackgroundColor,
        this.primaryColor
      );
    },
    selectedSlotTextColor() {
      return this.servicesTheme.selectedServiceTextColor || this.textColor;
    },
    nextButtonStyle() {
      const services = this.servicesTheme;
      if (!this.selectedSlot) {
        return {
          background: '#c8c8c8',
          color: '#ffffff'
        };
      }
      return {
        background: services.mainButtonBackground || '#e8e4e1',
        color: services.mainButtonTextColor || this.primaryColor,
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)'
      };
    },
    phoneThemeTemplate() {
      if (this.themeTemplate && typeof this.themeTemplate === 'object') {
        return this.themeTemplate;
      }
      return {
        primaryColor: this.primaryColor,
        secondaryColor: this.secondaryColor,
        textColor: this.textColor,
        numPad: {
          numPrimary: this.primaryColor,
          numSecondary: this.secondaryColor,
          numBackground: '#ffffff',
          sendButtonColor: this.primaryColor,
          sendButtonBackground: '#e8e4e1',
          anonymSendButtonColor: this.primaryColor,
          anonymSendButtonBackground: '#e8e4e1'
        }
      };
    }
  },
  created() {
    const today = new Date();
    this.weekStart = this.startOfWeekMonday(today);
    this.selectedDate = new Date(today);
    
    // Always auto-select the first department
    if (this.departments && this.departments.length > 0) {
      this.selectedDepartment = this.departments[0];
    }
  },
  mounted() {
    this._slotGridObserver = new ResizeObserver(() => {
      this.updateSlotGridLayout();
    });
    this.$nextTick(() => {
      if (this.$refs.timeslotsArea) {
        this._slotGridObserver.observe(this.$refs.timeslotsArea);
      }
      this.updateSlotGridLayout();
    });
  },
  beforeUnmount() {
    if (this._slotGridObserver) {
      this._slotGridObserver.disconnect();
    }
  },
  methods: {
    scheduleSlotGridLayout() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.updateSlotGridLayout();
        });
      });
    },
    updateSlotGridLayout() {
      const area = this.$refs.timeslotsArea;
      const count = this.timeSlots.length;

      if (!area || count === 0 || this.slotsLoading || this.step !== 0) {
        return;
      }

      const gap = window.innerWidth <= 768 ? 8 : 14;
      const padX = 12;
      const padY = 12;
      const maxSlotHeight = 68;
      const width = area.clientWidth - padX;
      const height = area.clientHeight - padY;

      if (width <= 0 || height <= 0) {
        return;
      }

      if (count === 1) {
        const virtualCount = 2;
        let bestCols = 1;
        let bestCellSize = 0;

        for (let cols = 1; cols <= virtualCount; cols++) {
          const rows = Math.ceil(virtualCount / cols);
          const cellW = (width - (cols - 1) * gap) / cols;
          const cellH = Math.min(
            maxSlotHeight,
            (height - (rows - 1) * gap) / rows
          );
          const cellSize = Math.min(cellW, cellH);

          if (cellSize > bestCellSize) {
            bestCellSize = cellSize;
            bestCols = cols;
          }
        }

        const rows = Math.ceil(virtualCount / bestCols);
        const cellW = (width - (bestCols - 1) * gap) / bestCols;
        const cellH = Math.min(
          maxSlotHeight,
          (height - (rows - 1) * gap) / rows
        );

        this.isSingleSlotLayout = true;
        this.singleSlotWidth = `${Math.round(cellW)}px`;
        this.singleSlotHeight = `${Math.round(cellH)}px`;
        this.slotGridCols = 1;
        this.slotGridRows = 1;

        const fontSize = Math.max(14, Math.min(28, Math.min(cellW, cellH) * 0.38));
        this.slotFontSize = `${Math.round(fontSize)}px`;
        return;
      }

      this.isSingleSlotLayout = false;

      let bestCols = 1;
      let bestCellSize = 0;

      for (let cols = 1; cols <= count; cols++) {
        const rows = Math.ceil(count / cols);
        const cellW = (width - (cols - 1) * gap) / cols;
        const cellH = Math.min(
          maxSlotHeight,
          (height - (rows - 1) * gap) / rows
        );
        const cellSize = Math.min(cellW, cellH);

        if (cellSize > bestCellSize) {
          bestCellSize = cellSize;
          bestCols = cols;
        }
      }

      const rows = Math.ceil(count / bestCols);
      this.slotGridCols = bestCols;
      this.slotGridRows = rows;

      const cellW = (width - (bestCols - 1) * gap) / bestCols;
      const cellH = Math.min(
        maxSlotHeight,
        (height - (rows - 1) * gap) / rows
      );
      const fontSize = Math.max(14, Math.min(28, Math.min(cellW, cellH) * 0.38));
      this.slotFontSize = `${Math.round(fontSize)}px`;
    },
    getDepartmentLabel(dept) {
      if (dept.translations) {
        const translations = typeof dept.translations === 'string' 
          ? JSON.parse(dept.translations) 
          : dept.translations;
        return translations[this.$i18n.locale] || dept.title || dept.name;
      }
      return dept.title || dept.name;
    },
    getServiceLabel(service) {
      if (!service) return '';
      if (service.translations) {
        const translations = typeof service.translations === 'string'
          ? JSON.parse(service.translations)
          : service.translations;
        return translations[this.$i18n.locale] || service.title || service.name || '';
      }
      return service.title || service.name || '';
    },
    resolveThemeBackground(value, fallback = '#ffffff') {
      if (!value) return fallback;
      if (String(value).includes('base64')) {
        return `url(${value})`;
      }
      return value;
    },
    getDepartmentStyle(dept) {
      const isSelected = this.selectedDepartment && this.selectedDepartment.id === dept.id;
      if (isSelected) {
        return {
          background: this.selectedSlotBackground,
          color: this.selectedSlotTextColor,
          border: 'none'
        };
      }
      return {
        background: this.slotBackground,
        color: this.slotTextColor,
        border: 'none',
        cursor: 'pointer'
      };
    },
    getDepartmentStyleHorizontal(dept) {
      const isSelected = this.selectedDepartment && this.selectedDepartment.id === dept.id;
      if (isSelected) {
        return {
          background: this.selectedSlotBackground,
          color: this.selectedSlotTextColor,
          border: 'none',
          fontWeight: '400'
        };
      }
      return {
        background: this.slotBackground,
        color: this.slotTextColor,
        border: 'none',
        cursor: 'pointer',
        fontWeight: '400'
      };
    },
    selectDepartment(dept) {
      if (this.slotsLoading) return;
      this.selectedDepartment = dept;
      this.selectedSlot = null;
      this.slotsFetchError = null;
      // Fetch available slots for the selected department and date
      this.fetchAvailableSlots();
    },
    startOfWeekMonday(d) {
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      const day = x.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      x.setDate(x.getDate() + diff);
      return x;
    },
    addDays(d, n) {
      const x = new Date(d);
      x.setDate(x.getDate() + n);
      return x;
    },
    isSameDay(a, b) {
      if (!a || !b) return false;
      return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    },
    isPastDay(d) {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      const x = new Date(d);
      x.setHours(0, 0, 0, 0);
      return x < t;
    },
    dateInWeek(d) {
      return this.weekDays.some((dt) => this.isSameDay(dt, d));
    },
    shortWeekday(d) {
      let loc = this.$i18n.locale;
      if (loc === 'ar') loc += '-tn';
      return moment(d).locale(loc).format('ddd');
    },
    colorWithOpacity(color, alpha) {
      if (!color) return `rgba(156, 156, 156, ${alpha})`;
      if (color.startsWith('rgba')) return color;
      if (color.startsWith('rgb')) {
        const match = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
        if (match) return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
      }
      const hex = String(color).replace('#', '');
      const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
      const n = parseInt(full, 16);
      const r = (n >> 16) & 255;
      const g = (n >> 8) & 255;
      const b = n & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    dayDomStyle(d) {
      if (this.isSameDay(d, this.selectedDate)) {
        return { background: this.secondaryColor, color: this.textColor };
      }
      return {
        // color: this.colorWithOpacity(this.secondaryColor, 0.55),
        color:this.headingColor,  
      };
    },
    dayDowStyle(d) {
      if (this.isSameDay(d, this.selectedDate)) {
        return { color: this.secondaryColor };
      }
     return {
        // color: this.colorWithOpacity(this.secondaryColor, 0.55),
        color:this.headingColor,
      };
    },
    getSelectableDaysForWeekStart(weekStart) {
      const days = [];
      for (let i = 0; i < 7; i++) {
        const d = this.addDays(weekStart, i);
        if (!this.isPastDay(d)) days.push(d);
      }
      return days;
    },
    navigateWeek(direction) {
      if (this.slotsLoading) return;
      const delta = direction === 'next' ? 7 : -7;
      const newWeekStart = this.addDays(this.weekStart, delta);
      const candidate = this.addDays(this.selectedDate, delta);
      let dayToSelect = null;
      if (!this.isPastDay(candidate)) {
        dayToSelect = candidate;
      } else {
        const selectable = this.getSelectableDaysForWeekStart(newWeekStart);
        dayToSelect = direction === 'next'
          ? selectable[0]
          : selectable[selectable.length - 1];
      }
      if (dayToSelect) {
        this.selectDay(dayToSelect);
      } else {
        this.weekStart = newWeekStart;
        this.selectedSlot = null;
      }
    },
    goPrevWeek() {
      this.navigateWeek('prev');
    },
    goNextWeek() {
      this.navigateWeek('next');
    },
    selectDay(d) {
      if (this.slotsLoading || this.isPastDay(d)) return;
      this.selectedDate = new Date(d);
      this.weekStart = this.startOfWeekMonday(d);
      this.selectedSlot = null;
      this.slotsFetchError = null;
      
      // Fetch slots when department is selected
      if (this.selectedDepartment) {
        this.fetchAvailableSlots();
      }
    },
    generateTimeSlots() {
      const slots = [];
      const start = this.startHour * 60;
      const end = this.endHour * 60;
      const now = new Date();
      const sel = new Date(this.selectedDate);
      sel.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isToday = sel.getTime() === today.getTime();

      for (let minutes = start; minutes < end; minutes += this.interval) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const timeValue = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;

        let disabled = this.disabledSlots.includes(timeValue);
        if (isToday && this.hidePastSlots) {
          const slotStart = new Date(this.selectedDate);
          slotStart.setHours(hours, mins, 0, 0);
          if (slotStart < now) {
            disabled = true;
          }
        }

        slots.push({
          value: timeValue,
          label: timeValue,
          disabled
        });
      }

      this.timeSlots = slots;
    },
    fetchAvailableSlots() {
      this.slotsFetchError = null;

      if (!this.selectedDepartment || !this.selectedDate) {
        console.log('Cannot fetch slots - missing department or date', {
          department: this.selectedDepartment,
          date: this.selectedDate
        });
        this.generateTimeSlots();
        return;
      }

      const business = JSON.parse(window.localStorage.getItem('business'));
      if (!business || !business.id) {
        console.error('Business information not found');
        this.generateTimeSlots();
        return;
      }

      const payload = {
        business_id: business.id,
        date: moment(this.selectedDate).format('YYYY-MM-DD'),
        department_id: this.selectedDepartment.id,
        user: null
      };

      console.log('Fetching available slots with payload:', payload);

      this.slotsLoading = true;

      kioskService.getAvailableSlots(payload)
        .then((res) => {
          console.log('API response:', res);
          if (res && res.slots && Array.isArray(res.slots)) {
            this.slotsFetchError = null;
            // Map API response to time slots format
            const now = new Date();
            const sel = new Date(this.selectedDate);
            sel.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isToday = sel.getTime() === today.getTime();

            this.timeSlots = res.slots.map(slot => {
              // API returns 'slot' and 'active' fields
              let disabled = !slot.active;
              
              // Also check if slot is in the past for today
              if (isToday && this.hidePastSlots && slot.slot) {
                const [hours, mins] = slot.slot.split(':').map(Number);
                const slotStart = new Date(this.selectedDate);
                slotStart.setHours(hours, mins, 0, 0);
                if (slotStart < now) {
                  disabled = true;
                }
              }

              return {
                value: slot.slot,
                label: slot.slot,
                disabled
              };
            });
            console.log('Mapped time slots:', this.timeSlots);
          } else {
            // Fallback to generated slots if API doesn't return expected format
            console.warn('API response format unexpected, using generated slots');
            this.generateTimeSlots();
          }
        })
        .catch((error) => {
          console.error('Error fetching available slots:', error);
          this.timeSlots = [];
          this.selectedSlot = null;
          if (error && error.status === 400) {
            this.slotsFetchError = this.$t('Kiosk.noSlots');
          } else {
            this.slotsFetchError = null;
            this.generateTimeSlots();
          }
        })
        .finally(() => {
          this.slotsLoading = false;
        });
    },
    selectSlot(slot) {
      if (!slot.disabled) {
        this.selectedSlot = slot.value;
      }
    },
    getSlotStyle(slot) {
      if (slot.disabled) {
        return {
          background: this.slotBackground,
          color: this.slotTextColor,
          opacity: 0.5,
          cursor: 'not-allowed',
          border: 'none'
        };
      }
      if (this.selectedSlot === slot.value) {
        return {
          background: this.selectedSlotBackground,
          color: this.selectedSlotTextColor,
          border: 'none'
        };
      }
      return {
        background: this.slotBackground,
        color: this.slotTextColor,
        border: 'none',
        cursor: 'pointer'
      };
    },
    handleNext() {
      if (this.step === 0 && this.selectedSlot) {
        this.step = 1;
      }
    },
    onPhonePadSend(payload) {
      if (!payload || !payload.phone_number || String(payload.phone_number).length !== 8) {
        return;
      }
      
      // Set loading message and start loading
      try {
        this.loadingMessage = this.$t('Kiosk.generatingTicket');
      } catch (ex) {
        this.loadingMessage = "Veuillez patienter, nous sommes en train de générer votre ticket";
      }
      this.isCreatingBooking = true;
      
      const prefix = (payload.country_prefix || '+216').replace(/\s/g, '');
      const phoneNumber = `${prefix}${payload.phone_number}`;
      
      const business = JSON.parse(window.localStorage.getItem('business'));
      const kiosk = JSON.parse(window.localStorage.getItem('kiosk'));
      
      if (!business || !business.id) {
        console.error('Business information not found');
        this.isCreatingBooking = false;
        return;
      }
      
      // First, search for the customer
      const searchPayload = {
        country_prefix: prefix,
        internal_id: null,
        phone_number: payload.phone_number
      };
      
      console.log('Searching for customer with payload:', searchPayload);
      
      kioskService.searchClient(searchPayload)
        .then((searchResponse) => {
          console.log('Customer search response:', searchResponse);
          
          let userId = null;
          let member_id = null;
          
          // Check if customer exists in the response
          if (searchResponse && searchResponse.customer && Array.isArray(searchResponse.customer) && searchResponse.customer.length > 0) {
            // Customer found, use their ID
            userId = searchResponse.customer[0].id;
            member_id = searchResponse.customer[0].bookings && searchResponse.customer[0].bookings[0] 
              ? searchResponse.customer[0].bookings[0].member_id 
              : null;
            console.log('Customer found with ID:', userId, 'member_id:', member_id);
            
            // Proceed to create booking
            return Promise.resolve({ userId, member_id });
          } else {
            // Customer not found, create new customer
            console.log('No customer found, creating new customer');
            const createCustomerPayload = {
              country: 'TN',
              country_prefix: prefix,
              email: '',
              fname: 'anonyme',
              internal_id: null,
              lname: ' ',
              phone_number: payload.phone_number
            };
            
            return kioskService.createCustomer(createCustomerPayload)
              .then((createResponse) => {
                console.log('Customer created:', createResponse);
                userId = createResponse.customer?.id || createResponse.id;
                console.log('New customer ID:', userId);
                return { userId, member_id: null };
              });
          }
        })
        .then(({ userId, member_id }) => {
          // Construct the booking payload matching the API requirements
          // Format services - use selectedService prop if available, otherwise use department services
          let servicesString = "";
          if (this.selectedService && this.selectedService.id) {
            // Use the selected service
            servicesString = `${this.selectedService.id}-qte:1`;
          } else if (this.selectedDepartment.services) {
            // Fallback to department services
            if (Array.isArray(this.selectedDepartment.services)) {
              servicesString = this.selectedDepartment.services
                .map(service => `${service.id}-qte:1`)
                .join(',');
            } else if (typeof this.selectedDepartment.services === 'string') {
              servicesString = this.selectedDepartment.services;
            }
          }
          
          const bookingPayload = {
            business_id: business.id,
            department_id: this.selectedDepartment.id,
            end_datetime: `${moment(this.selectedDate).format('YYYY-MM-DD')} ${this.selectedSlot}`,
            member_id: member_id,
            services: servicesString,
            start_datetime: `${moment(this.selectedDate).format('YYYY-MM-DD')} ${this.selectedSlot}`,
            user_id: userId,
            phone_number: phoneNumber
          };
          
          console.log('Creating booking with payload:', bookingPayload);
          
          return kioskService.createBooking(bookingPayload);
        })
        .then((response) => {
          console.log('Booking created successfully:', response);
          
          // Stop loading
          this.isCreatingBooking = false;
          
          // Check if status code is 201 and show success popup
          if (response._statusCode === 201) {
            console.log('✅ Booking created with status 201 - SUCCESS!');
            // Show success popup
            this.showSuccessPopup = true;
            
            // Store the booking data to emit after popup closes
            this.bookingData = {
              department: this.selectedDepartment,
              date: moment(this.selectedDate).format('YYYY-MM-DD'),
              timeSlot: this.selectedSlot,
              phoneNumber,
              booking: response
            };
          } else {
            // If not 201, emit immediately
            this.$emit('confirm', {
              department: this.selectedDepartment,
              date: moment(this.selectedDate).format('YYYY-MM-DD'),
              timeSlot: this.selectedSlot,
              phoneNumber,
              booking: response
            });
          }
        })
        .catch((error) => {
          console.error('Error in booking process:', error);
          this.isCreatingBooking = false;
          
          // Check if error is 409 status code (booking quota exhausted)
          if (error && error.status === 409) {
            console.log('Booking quota exhausted - showing error popup');
            this.showErrorMessage(this.$t('maxTicket'));
          } else {
            // Show generic error message
            this.showErrorMessage(this.$t('Kiosk.uknownError'));
          }
        });
    },
    handleBack() {
      if (this.step === 1) {
        this.step = 0;
        return;
      }
      this.closePopup();
    },
    handleSuccessClose() {
      this.showSuccessPopup = false;
      
      // Emit the confirm event AFTER the success popup is closed
      if (this.bookingData) {
        this.$emit('confirm', this.bookingData);
        this.bookingData = null;
      }
      
      // Then close the main popup
      this.closePopup();
    },
    showErrorMessage(message) {
      this.errorMessage = message;
      this.showErrorPopup = true;
    },
    closeErrorPopup() {
      this.showErrorPopup = false;
      this.errorMessage = '';
      // Close the time slot popup and return to main screen
      this.closePopup();
    },
    closePopup() {
      const today = new Date();
      this.weekStart = this.startOfWeekMonday(today);
      this.selectedDate = new Date(today);
      this.step = 0;
      this.selectedSlot = null;
      
      // Always reset to first department
      if (this.departments && this.departments.length > 0) {
        this.selectedDepartment = this.departments[0];
      } else {
        this.selectedDepartment = null;
      }
      
      this.timeSlots = [];
      this.slotsFetchError = null;
      this.slotsLoading = false;
      this.$emit('update:modelValue', false);
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.timeslot-inline-root {
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.timeslot-popup--inline {
  width: 100%;
  height: 100%;
  max-width: none;
  max-height: none;
  flex: 1;
  min-height: 0;
  padding: 0;
}

.timeslot-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.timeslot-popup {
  font-family: 'Exo 2', serif;
  border-radius: 0;
  /* padding: 4vh 2vw; */
  box-shadow: none;
  display: flex;
  flex-direction: column;
}

.timeslot-popup--phone-step {
  padding: 0;
}

.timeslot-popup--phone-step .popup-actions {
  padding-top: 0.5vmin;
  flex-shrink: 0;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5vmin;
  flex-shrink: 0;
}

.header-spacer {
  width: 32px;
  flex-shrink: 0;
}

.popup-header--phone-step {
  justify-content: space-between;
  margin-bottom: 1.5vmin;
}

.popup-header--phone-step h2 {
  flex: 1;
  text-align: center;
}

.popup-header h2 {
  margin: 0;
  font-family: 'Exo 2', serif;
  font-size: clamp(1.25rem, 3vmin, 2rem);
  font-weight: 400;
  line-height: 1.3;
  flex: 1;
  text-align: center;
}

.close-btn {
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step1-flow {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 0.5vmin;
}

.departments-horizontal {
  display: flex;
  flex-direction: row;
  gap: 1vmin;
  padding: 1vmin 0 1.5vmin;
  flex-shrink: 0;
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  scrollbar-width: thin;
}

.departments-horizontal::-webkit-scrollbar {
  height: 4px;
}

.departments-horizontal::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 0;
}

.departments-horizontal::-webkit-scrollbar-thumb {
  background: v-bind(slotBorderAccent);
  border-radius: 0;
}

.department-item-horizontal {
  font-family: 'Exo 2', serif;
  padding: 1.2vmin 2.5vmin;
  border: none;
  border-radius: 0;
  text-align: center;
  font-size: clamp(0.85rem, 2.2vmin, 2.2rem);
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  cursor: pointer;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
}

.department-item-horizontal:hover {
  opacity: 0.92;
}

.department-item-horizontal:disabled,
.departments-horizontal.is-loading .department-item-horizontal {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}

.departments-container-inline {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 6px 4px;
  margin-bottom: 4px;
}

.department-item {
  font-family: 'Exo 2', serif;
  padding: 2vmin 1.5vmin;
  border-radius: 0;
  text-align: center;
  font-size: clamp(0.9rem, 2vmin, 1.15rem);
  font-weight: 400;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dept-label {
  display: block;
  line-height: 1.4;
}

.week-strip {
  flex-shrink: 0;
  padding: 0.75vmin 0 1.25vmin;
}

.week-strip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1vmin;
  padding: 0 0.5vmin;
  gap: 0.75vmin;
}

.week-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: clamp(42px, 5.5vmin, 60px);
  height: clamp(42px, 5.5vmin, 60px);
  border: none;
  border-radius: 0;
  background: v-bind(primaryColor);
  color: v-bind(textColor);
  cursor: pointer;
}

.week-strip.is-loading {
  opacity: 0.65;
  pointer-events: none;
}

.week-nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.day-cell:disabled {
  cursor: not-allowed;
}

.week-nav-icon {
  width: clamp(18px, 2.6vmin, 26px);
  height: clamp(18px, 2.6vmin, 26px);
  display: block;
}

.week-month-label {
  font-family: 'Exo 2', serif;
  font-size: clamp(1rem, 2.6vmin, 1.6rem);
  font-weight: 400;
  letter-spacing: 0.02em;
  line-height: 1.2;
  text-align: center;
  flex: 1;
  min-width: 0;
}

.week-days-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: clamp(3px, 0.8vmin, 8px);
  width: 100%;
}

.day-cell {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0.35vmin;
  padding: 0.6vmin 0.2vmin;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 0;
  font-family: 'Exo 2', serif;
}

/* .day-cell:not(:disabled):hover {
  background: rgba(0, 0, 0, 0.04);
} */

.day-cell--past {
  opacity: 0.4;
  cursor: not-allowed;
}

.day-cell-dow {
  font-size: clamp(0.78rem, 1.75vmin, 1.1rem);
  font-weight: 400;
  margin-bottom: 0.15vmin;
  text-transform: lowercase;
  line-height: 1.2;
}

.day-cell-dom {
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(40px, 6.2vmin, 66px);
  height: clamp(40px, 6.2vmin, 66px);
  border-radius: 0;
  font-size: clamp(1rem, 2.5vmin, 1.4rem);
  font-weight: 400;
  line-height: 1;
}

@media (min-width: 1200px) {
  .week-strip {
    padding: 1vmin 0 1.5vmin;
  }

  .week-strip-header {
    margin-bottom: 1.25vmin;
  }

  .week-nav-btn {
    width: clamp(46px, 3.5vw, 68px);
    height: clamp(46px, 3.5vw, 68px);
  }

  .week-nav-icon {
    width: clamp(20px, 1.8vw, 30px);
    height: clamp(20px, 1.8vw, 30px);
  }

  .week-month-label {
    font-size: clamp(1.1rem, 1.8vw, 1.85rem);
  }

  .day-cell-dow {
    font-size: clamp(0.85rem, 1.25vw, 1.15rem);
  }

  .day-cell-dom {
    width: clamp(44px, 4.2vw, 72px);
    height: clamp(44px, 4.2vw, 72px);
    font-size: clamp(1.05rem, 1.65vw, 1.5rem);
  }
}

.timeslots-container {
  display: grid;
  gap: 1.5vmin;
  flex: 1;
  min-height: 0;
  height: 100%;
  padding: 8px 6px 4px;
  margin-bottom: 4px;
  align-content: start;
  overflow-y: auto;
}

.timeslots-container--single {
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-department-message {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 3vmin 2vmin;
}

.no-department-message p {
  font-family: 'Exo 2', serif;
  font-size: clamp(1rem, 2.2vmin, 1.25rem);
  font-weight: 400;
  margin: 0;
  text-align: center;
}

.timeslots-area {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.timeslots-loading {
  flex: 1;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5vmin;
  padding: 32px 20px;
}

.timeslot-spinner {
  width: clamp(3rem, 8vmin, 5rem);
  height: clamp(3rem, 8vmin, 5rem);
  border: 0.35em solid rgba(127, 127, 127, 0.25);
  border-top-color: v-bind(primaryColor);
  border-right-color: v-bind(primaryColor);
  border-radius: 50%;
  animation: timeslot-spin 0.75s linear infinite;
}

@keyframes timeslot-spin {
  to {
    transform: rotate(360deg);
  }
}

.timeslots-loading__label {
  font-family: 'Exo 2', serif;
  font-size: clamp(0.9rem, 2vmin, 1.15rem);
  font-weight: 400;
  margin: 0;
  text-align: center;
}

.availability-error-message {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 32px 20px;
  text-align: center;
}

.availability-error-message p {
  font-family: 'Exo 2', serif;
  font-size: clamp(1rem, 2.2vmin, 1.25rem);
  font-weight: 400;
  margin: 0;
  line-height: 1.45;
  max-width: 42rem;
}

.phone-input-container {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  width: 100%;
  /* padding:  4vh 2vw; */
}

.phone-step-layout {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1;
}

.phone-info-panel {
  flex: 0 0 50%;
  max-width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0vmin 2vmin 0vmin 2vmin;
  min-height: 0;
  box-sizing: border-box;
}

.phone-info-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  text-align: left;
}

.phone-info-footer {
  flex-shrink: 0;
  padding-top: 2vmin;
}

.phone-keypad-panel {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* padding: 0vmin 2vmin 0vmin 2vmin; */
  overflow: hidden;
}

.phone-step-title {
  margin: 0 0 1.5vmin;
  font-family: 'Exo 2', serif;
  font-size: calc(2.5 * (1vh + 1vw) / 2);
  font-weight: 400;
  line-height: calc(3 * (1vh + 1vw) / 2);
  text-align: left;
}

.agreement-text {
  margin-top: 1.5vmin;
}

.appointment-recap {
  display: flex;
  margin-top: 4vmin;
  width: 100%;
}

.appointment-recap__accent {
  width: 4px;
  flex-shrink: 0;
}

.appointment-recap__content {
  flex: 1;
  min-width: 0;
  padding: 0.5vmin 0 0.5vmin 2vmin;
  text-align: left;
}

.appointment-recap__label {
  display: block;
  font-family: 'Exo 2', serif;
  font-size: calc(1.1 * (1vh + 1vw));
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1.3;
  margin-bottom: 1.2vmin;
}

.appointment-recap__service {
  font-family: 'Exo 2', serif;
  font-size: calc(1.35 * (1vh + 1vw));
  font-weight: 400;
  line-height: 1.35;
  margin: 0 0 0.6vmin;
}

.appointment-recap__date {
  font-family: 'Exo 2', serif;
  font-size: calc(1.25 * (1vh + 1vw));
  font-weight: 300;
  line-height: 1.4;
  margin: 0 0 1.5vmin;
  opacity: 0.95;
}

.appointment-recap__time-block {
  margin-top: 0.5vmin;
}

.appointment-recap__time {
  display: inline-block;
  font-family: 'Digital Numbers', monospace;
  font-size: clamp(2.2rem, 5.5vw, 4rem);
  line-height: 1;
  letter-spacing: 0.08em;
}

.time-text {
font-family: 'Exo 2', serif; 
letter-spacing: 0.04em;
}

.phone-hint {
  font-family: 'Exo 2', serif;
  font-size: calc(1 * (1vh + 1vw));
  font-weight: 300;
  margin: 0;
  text-align: left;
  line-height: calc(3 * (1vh + 1vw) / 2);
  opacity: 0.95;
}

.btn-back-phone {
  font-family: 'Exo 2', serif;
  width: 90%;
  height: 10vmin;
  border-radius: 0;
  background: transparent;
  font-size: calc(1.5vh + 1.5vw);
  font-weight: 400;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.phone-keypad-panel :deep(.phone-number-input-container) {
  height: 100%;
  min-height: 0;
}

.phone-keypad-panel :deep(.input-box) {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.phone-keypad-panel :deep(.pad-container) {
  flex: 1;
  min-height: 0;
  align-content: center;
}

.phone-keypad-panel :deep(.pad-element) {
  border-radius: 0;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
}

.phone-keypad-panel :deep(.phone-number-input) {
  border-radius: 0;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
}

.phone-keypad-panel :deep(.button-container) {
  flex-shrink: 0;
  margin-bottom: 0;
}

@media (max-width: 768px) {
  .timeslot-popup {
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    /* padding: 2vmin 2.5vmin; */
  }

  .departments-horizontal {
    flex-wrap: nowrap;
    gap: 0.8vmin;
  }

  .department-item-horizontal {
    flex: 0 0 auto;
    min-width: min(45%, 180px);
    padding: 1vmin 2vmin;
    font-size: clamp(0.8rem, 2.5vw, 1rem);
  }

  .week-days-row {
    gap: clamp(2px, 1vw, 8px);
  }

  .week-nav-btn {
    width: clamp(40px, 10vw, 64px);
    height: clamp(40px, 10vw, 64px);
  }

  .week-nav-icon {
    width: clamp(18px, 4vw, 28px);
    height: clamp(18px, 4vw, 28px);
  }

  .week-month-label {
    font-size: clamp(1rem, 3.5vw, 1.5rem);
  }

  .day-cell-dow {
    font-size: clamp(0.82rem, 2.8vw, 1.12rem);
  }

  .day-cell-dom {
    width: clamp(38px, 10vw, 58px);
    height: clamp(38px, 10vw, 58px);
    font-size: clamp(1rem, 3.8vw, 1.35rem);
  }

  .timeslot-popup--phone-step {
    padding: 0;
  }

  .phone-step-layout {
    flex-direction: column;
  }

  .phone-info-panel {
    flex: 0 0 auto;
    max-width: 100%;
    border-right: none !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.25);
    padding: 2.5vmin 3vmin 2vmin;
  }

  .phone-info-footer {
    padding-top: 1.5vmin;
  }

  .btn-back-phone {
    width: 100%;
    height: clamp(44px, 9vmin, 56px);
    font-size: clamp(0.9rem, 2.5vw, 1.1rem);
  }

  .phone-keypad-panel {
    flex: 1;
  }

  .appointment-recap {
    margin-top: 2.5vmin;
  }

  .appointment-recap__label {
    font-size: calc(1.5 * (1vh + 1vw));
    margin-bottom: 1vmin;
  }

  .appointment-recap__service {
    font-size: calc(1.8 * (1vh + 1vw));
  }

  .appointment-recap__date {
    font-size: calc(1.6 * (1vh + 1vw));
    margin-bottom: 1vmin;
  }

  .appointment-recap__time {
    font-size: clamp(1.8rem, 8vw, 2.8rem);
  }

  .phone-step-title {
    font-size: calc(2 * (1vh + 1vw));
    line-height: calc(2.5 * (1vh + 1vw));
  }

  .phone-hint {
    font-size: calc(1.6 * (1vh + 1vw));
    line-height: calc(2.5 * (1vh + 1vw));
  }

  .timeslots-container {
    gap: 0.8vmin;
    padding: 0.5vmin;
  }

  .popup-header h2 {
    font-size: clamp(1rem, 4vw, 1.35rem);
  }

  .popup-actions {
    flex-direction: row;
    gap: 1vmin;
  }

  .btn-cancel {
    max-width: none;
    flex: 1;
  }

  .btn-confirm {
    flex: 1;
  }
}

@media (max-width: 480px) {
  .week-nav-btn {
    width: clamp(36px, 11vw, 48px);
    height: clamp(36px, 11vw, 48px);
  }

  .week-month-label {
    font-size: clamp(0.95rem, 4vw, 1.2rem);
  }

  .day-cell-dow {
    font-size: clamp(0.78rem, 4.5vw, 1.05rem);
  }

  .day-cell-dom {
    width: clamp(34px, 11vw, 50px);
    height: clamp(34px, 11vw, 50px);
    font-size: clamp(0.95rem, 4.5vw, 1.2rem);
  }

  .popup-actions {
    flex-direction: column-reverse;
  }

  .btn-cancel,
  .btn-confirm {
    width: 100%;
    max-width: none;
  }
}

.timeslot-item {
  font-family: 'Exo 2', serif;
  padding: 0.8vmin 0.5vmin;
  border-radius: 0;
  text-align: center;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: clamp(44px, 7vmin, 64px);
  max-height: clamp(52px, 8vmin, 72px);
  height: clamp(48px, 7.5vmin, 68px);
  cursor: pointer;
  filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
}

.timeslot-item:not(:disabled):hover {
  opacity: 0.92;
}

.timeslot-item:disabled {
  cursor: not-allowed;
}

.slot-time {
  display: block;
}

.popup-actions {
  display: flex;
  gap: 1.5vmin;
  justify-content: stretch;
  flex-shrink: 0;
  padding-top: 1.5vmin;
  width: 100%;
}

.btn-cancel,
.btn-confirm {
  font-family: 'Exo 2', serif;
  flex: 1 1 0;
  min-width: 0;
  width: 50%;
  padding: 0;
  height: clamp(48px, 10vmin, 64px);
  border-radius: 0;
  font-size: calc(1.3vh + 1.3vw);
  font-weight: 400;
  cursor: pointer;
  border: none;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.btn-cancel {
  background: transparent;
}

.btn-cancel:hover,
.btn-confirm:not(:disabled):hover {
  opacity: 0.9;
}

.btn-confirm:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>
